import { Link } from "react-router-dom";
import { site } from "../data/site";
import PageHero from "../components/PageHero";
import Button from "../components/Button";
import { IconArrow } from "../components/icons";
import { CompassRose, ContourLines, ScaleBar } from "../components/Glyphs";
import useReveal from "../components/useReveal";
import styles from "./About.module.css";

export default function About() {
  useReveal();
  return (
    <>
      <PageHero
        eyebrow="The Studio"
        code="A · 01"
        title="A small studio. Hands on every drawing."
        subtitle="UPSHOT Architecture is led by Sten Witmer — a Montana-licensed architect with a Montana State University degree and the patience to detail a window jamb. The studio works with clients who want to be in the conversation, not handed a deck."
      />

      <section className={styles.bio}>
        <div className={[styles.bioInner, "reveal"].join(" ")}>
          <div className={styles.bioImg}>
            <img
              src="/photos/portrait-sten.jpg"
              alt="Sten Witmer, Architect, principal of UPSHOT Architecture."
              loading="eager"
            />
            <div className={styles.bioCaption}>
              <span className={styles.bioCapName}>{site.principal}</span>
              <span className={styles.bioCapMeta}>NCARB · AIA · MSU</span>
            </div>
          </div>
          <div className={styles.bioCopy}>
            <span className={styles.eyebrow}>The Principal</span>
            <h2 className={styles.h2}>
              An architect who's spent as much time on a job site as in the studio.
            </h2>
            <p>
              Sten founded UPSHOT after years split between design firms and the
              construction trades. The studio's perspective is the result: a
              design practice that thinks in sections and details, that knows
              what the framer needs at 3pm on a Friday, and that takes seriously
              the gap between a beautiful render and a building that ages well.
            </p>
            <p>
              He's a graduate of Montana State University's School of Architecture,
              licensed in Montana, and a member of the American Institute of
              Architects. The studio is based in Bozeman and works across
              Southwest Montana.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.philosophy}>
        <ContourLines
          className={[styles.philContours, "reveal"].join(" ")}
          color="#1a1d20"
          width={1200}
          height={420}
        />
        <div className={[styles.philInner, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>Philosophy</span>
          <h2 className={styles.h2}>
            We design for permanence — and for the actual site.
          </h2>
          <div className={styles.philGrid}>
            <article>
              <span className={styles.philCode}>01</span>
              <h3>Site First</h3>
              <p>
                A building is the agreement between a piece of land and the
                people who'll use it. Before we draw, we walk the site — slope,
                wind, sightlines, the way the sun moves through October.
              </p>
            </article>
            <article>
              <span className={styles.philCode}>02</span>
              <h3>Detail Drives Design</h3>
              <p>
                Big moves are easy. The work is in the small joints — where wood
                meets steel, where the deck breaks from the wall, where light
                spills across a cabinet face at 7am.
              </p>
            </article>
            <article>
              <span className={styles.philCode}>03</span>
              <h3>Built to Outlast Trends</h3>
              <p>
                We choose materials that age gracefully and details that don't
                expire with a trend cycle. Twenty years from now, the building
                should still feel inevitable.
              </p>
            </article>
            <article>
              <span className={styles.philCode}>04</span>
              <h3>Honest with Budget</h3>
              <p>
                We tell you what something costs before it gets drawn — and we
                value-engineer with the contractor in the room, not as an
                afterthought after the bid comes back.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.factsBand}>
        <CompassRose className={styles.factsCompass} size={120} color="#d8c9a8" />
        <div className={[styles.factsInner, "reveal"].join(" ")}>
          <h2 className={styles.factsTitle}>The studio at a glance.</h2>
          <ul className={styles.factsList}>
            <li>
              <span className={styles.factCode}>BASED</span>
              <span className={styles.factVal}>{site.city}</span>
            </li>
            <li>
              <span className={styles.factCode}>FOUNDED</span>
              <span className={styles.factVal}>{site.founded}</span>
            </li>
            <li>
              <span className={styles.factCode}>LICENSED</span>
              <span className={styles.factVal}>Montana</span>
            </li>
            <li>
              <span className={styles.factCode}>MEMBERSHIP</span>
              <span className={styles.factVal}>AIA</span>
            </li>
            <li>
              <span className={styles.factCode}>FOCUS</span>
              <span className={styles.factVal}>Residential · Commercial</span>
            </li>
            <li>
              <span className={styles.factCode}>SERVICE AREA</span>
              <span className={styles.factVal}>Southwest Montana</span>
            </li>
          </ul>
          <ScaleBar width={260} color="#d8c9a8" label="UPSHOT · A1.0 · ESTABLISHED" />
        </div>
      </section>

      <section className={styles.cta}>
        <div className={[styles.ctaInner, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>Next</span>
          <h2 className={styles.h2}>See what we've been making.</h2>
          <div className={styles.ctaRow}>
            <Button to="/projects" variant="primary" size="lg">
              View Selected Work <IconArrow />
            </Button>
            <Link to="/contact" className={styles.ctaLink}>
              or get in touch <IconArrow />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
